import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { 
  Eye, 
  EyeOff, 
  GraduationCap, 
  Mail, 
  Lock, 
  ArrowRight,
  User,
  Users,
  Shield
} from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeTab, setActiveTab] = useState('student');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    studentId: '',
    admissionNumber: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const portals = [
    {
      id: 'student',
      name: 'Student',
      icon: User,
      description: 'Access your grades, assignments, and academic records',
      placeholder: 'student@bridgeoflife.edu'
    },
    {
      id: 'parent',
      name: 'Parent',
      icon: Users,
      description: 'Monitor your child\'s progress and school activities',
      placeholder: 'parent@bridgeoflife.edu'
    },
    {
      id: 'staff',
      name: 'Staff',
      icon: Shield,
      description: 'Manage classes, students, and administrative tasks',
      placeholder: 'staff@bridgeoflife.edu'
    }
  ];

  // Demo credentials commented out for production
  // const demoCredentials = [
  //   {
  //     portal: 'Student Portal',
  //     email: 'student@bridgeoflife.edu',
  //     password: 'demo123'
  //   },
  //   {
  //     portal: 'Parent Portal',
  //     email: 'parent@bridgeoflife.edu',
  //     password: 'demo123'
  //   },
  //   {
  //     portal: 'Staff Portal',
  //     email: 'staff@bridgeoflife.edu',
  //     password: 'demo123'
  //   }
  // ];

  // const features = [
  //   'View Grades',
  //   'School Calendar',
  //   'Check Assignments',
  //   'Announcements'
  // ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // For student portal, use studentId + admissionNumber
      let response;
      if (activeTab === 'student') {
        if (!formData.studentId || !formData.admissionNumber) {
          setError('Please provide both Student ID and Admission Number');
          setLoading(false);
          return;
        }
        // Student login with Student ID + Admission Number
        response = await login(null, null, formData.studentId, formData.admissionNumber);
      } else {
        // Regular email + password login for parent and staff
        if (!formData.email || !formData.password) {
          setError('Please provide both email and password');
          setLoading(false);
          return;
        }
        response = await login(formData.email, formData.password);
      }
      
      // Navigate based on user role
      const userRole = response.data.user.role;
      if (userRole === 'student') {
        navigate('/student-dashboard');
      } else if (userRole === 'parent') {
        navigate('/parent-dashboard');
      } else if (userRole === 'teacher' || userRole === 'staff' || userRole === 'admin') {
        // All non-student, non-parent roles use staff dashboard
        navigate('/dashboard/staff');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Something went wrong!. Please try again later!.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const activePortal = portals.find(p => p.id === activeTab);
  const ActiveIcon = activePortal?.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%236366f1%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-y-12"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-bounce"></div>
      <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-yellow-200 to-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-bounce" style={{animationDelay: '2s'}}></div>
      <div className="absolute bottom-20 left-40 w-28 h-28 bg-gradient-to-r from-green-200 to-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-bounce" style={{animationDelay: '4s'}}></div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mr-4 shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-900">Bridge of Life Academy</h1>
              <p className="text-gray-600">Student Information System</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Access your personalized dashboard to manage your academic journey
          </p>
        </motion.div>

        <div className="max-w-md mx-auto">
          {/* Portal Selection Tabs */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-2 shadow-lg border border-gray-200 mb-8"
          >
            <div className="grid grid-cols-3 gap-2">
              {portals.map((portal) => {
                const Icon = portal.icon;
                return (
                  <button
                    key={portal.id}
                    onClick={() => setActiveTab(portal.id)}
                    className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl transition-all duration-300 ${
                      activeTab === portal.id
                        ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{portal.name}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="p-8 shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                    <ActiveIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {activePortal?.name} Portal
                  </h2>
                </div>
                <p className="text-gray-600">
                  {activePortal?.description}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {activeTab === 'student' ? (
                  <>
                    {/* Student ID and Admission Number Login */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Student ID *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type="text"
                          name="studentId"
                          value={formData.studentId}
                          onChange={handleChange}
                          placeholder="Enter your Student ID (e.g., ST2025A001)"
                          className="pl-10 h-12 border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Password *
                      </label>
                      <p className="text-xs text-gray-500 mb-1">
                        Your password is your Admission Number
                      </p>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type="text"
                          name="admissionNumber"
                          value={formData.admissionNumber}
                          onChange={handleChange}
                          placeholder="Enter your Admission Number (e.g., BRI2025111)"
                          className="pl-10 h-12 border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Email and Password Login (for Parent and Staff) */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={activePortal?.placeholder || "Enter your email"}
                          className="pl-10 h-12 border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Password *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter your password"
                          className="pl-10 pr-10 h-12 border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all duration-200 transform hover:scale-[1.02]"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>Sign In</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </Button>

                <div className="text-center space-y-2">
                  <button
                    type="button"
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium transition-colors"
                  >
                    Forgot your password?
                  </button>
                  <div className="text-sm text-gray-500">
                    Don't have an account?{" "}
                    <button
                      type="button"
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Contact Administrator
                    </button>
                  </div>
                </div>
              </form>
            </Card>
          </motion.div>

        

          {/* Demo Credentials - Commented out for production */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6"
          >
            <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Demo Credentials
                </h3>
                <p className="text-gray-600 text-sm">
                  Use these credentials to explore the platform
                </p>
              </div>
              <div className="space-y-3">
                {demoCredentials.map((demo, index) => (
                  <motion.div
                    key={demo.portal}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border border-green-200"
                  >
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                      {demo.portal}
                    </h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">
                          {demo.email}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Password:</span>
                        <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">
                          {demo.password}
                        </code>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div> */}
        </div>
      </div>
    </div>
  );
};

export default Login;